import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { PackageConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers } from "src/components/shared/TravelersField/typings";
import { DateState, TravelersState } from "stores/wizard/state/global";
import { dateState } from "stores/wizard/state/global/date";
import { locationState, hotelLocationState } from "src/stores/wizard/state/global/location";
import { Location } from "src/stores/wizard/state/global/location/typings";
import { setCookie } from "components/utility/cookie";
import { setHotelPartialStayDate } from "stores/wizard/state/global/date/dateUtils";
import { LastSearchData, PackageLastSearchData } from "stores/wizard/LastSearchData";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { FlightLegState } from "../flight/MultiLegFlights/typings";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { formatDate } from "bernie-l10n";
import { PackageExperiment } from "src/components/flexComponents/WizardPackagePWA/typings";
import { locKeys } from "src/components/flexComponents/WizardPackagePWA/l10n";
import { FlightTypeCodeMap } from "../flight/typings";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { FlightType, PackagesSubLobs } from "../typings";
import { ClassCodes } from "src/components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirline } from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { travelersStateOnConfig } from "../global/travelers/travelersState";

export class PackageWizardState implements LOBState {
  private globalState: GlobalWizardState;

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  public localDateFormat: string;

  @action
  public buildMultiCityFlightLegUrl = (legIndex: number) => {
    const rawFormatForSubmission = this.config.date.ISOSubmissionSupport
      ? this.config.date.format
      : this.localDateFormat;

    const legOrigin = this.listOfAdditionalLegs[legIndex].location.origin;
    const legDestination = this.listOfAdditionalLegs[legIndex].location.destination;
    const startDate = formatDate(this.listOfAdditionalLegs[legIndex].date.start, {
      raw: rawFormatForSubmission,
    });

    return `orig:${legOrigin.metaData.ttla};origId:${legOrigin.metaData.regionId};dest:${legDestination.metaData.ttla};destId:${legDestination.metaData.regionId};departure:${startDate}`;
  };

  @action
  public updateLegLocationOrigin(selection: TypeaheadSelection, index: number) {
    this.updateLegLocation(selection, index, this.listOfAdditionalLegs[index].location.origin);
  }

  @action
  public updateLegLocationDestination(selection: TypeaheadSelection, index: number) {
    this.updateLegLocation(selection, index, this.listOfAdditionalLegs[index].location.destination);
  }

  @action
  public updateLegLocation(selection: TypeaheadSelection, index: number, locationToUpdate: Location) {
    const { term, data } = selection as TypeaheadSelectionProps;
    locationToUpdate.value = term;
    locationToUpdate.metaData.regionId = data?.regionId;
    locationToUpdate.metaData.ttla = data?.hierarchyInfo?.airport?.airportCode;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  }

  @action
  public swapOriginAndDestinationLocations(index: number) {
    const legToUpdate = this.listOfAdditionalLegs[index];
    const originalOrigin = legToUpdate.location.origin;
    const originalDestination = legToUpdate.location.destination;
    legToUpdate.location.origin = originalDestination;
    legToUpdate.location.destination = originalOrigin;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  }

  @action
  public updateLegDate = (start: Date, end: Date, index: number) => {
    const legToUpdate = this.listOfAdditionalLegs[index];
    const previousDate = new Date(legToUpdate.date.start);
    legToUpdate.date.start = start;
    legToUpdate.date.end = end;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
    this.updateLegsDates(previousDate, start, end, index);
    this.updatePackageDates();
    this.validateDepatureDates();
  };

  private updateLegsDates(previousDate: Date, start: Date, end: Date, index: number) {
    const previousDepartureDate = this.newDateFrom(previousDate);
    for (let i = index; i < this.listOfAdditionalLegs.length; i++) {
      const legDateState = this.listOfAdditionalLegs[i].date;
      const legDate = this.newDateFrom(legDateState.start);
      if (legDate.getTime() === previousDepartureDate.getTime()) {
        this.listOfAdditionalLegs[i].date.start = start;
        this.listOfAdditionalLegs[i].date.end = end;

        this.multiCityFlightLegURL[i] = this.buildMultiCityFlightLegUrl(i);
      }
    }
  }

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get hotelLocation() {
    return this.globalState.hotelLocation;
  }

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  public lastSearchData?: PackageLastSearchData;

  @observable
  public config: PackageConfig;

  @observable
  public staySomewhereElse = false;

  @observable
  public driveSomewhereElse = false;

  @observable
  public partialStay = false;

  @observable
  public onewayFlight = false;

  @observable
  public experimentNewPackageMulticityFlightType: PackageExperiment;

  @observable
  public experimentNewPackageOnewayFlightType: PackageExperiment;

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  @observable
  public numberOfErrors = 0;

  @observable
  public isHotelCarFormValid = true;

  @observable
  public isFlightCarFormValid = true;

  @observable
  public isFlightHotelCarFormValid = true;

  @observable
  public isFlightHotelDefaultFormValid = true;

  @observable
  public isFlightHotelOneWayFormValid = true;

  @observable
  public isFlightHotelMultiFormValid = true;

  @computed
  public get flightClassCode() {
    return this.globalState.flightClassCode;
  }

  @computed
  public get flightAirline() {
    return this.globalState.flightAirline;
  }

  @computed
  public get flightTypeCode() {
    return this.globalState.flightTypeCode;
  }

  @observable
  public subLOBState = "";

  @observable
  public subLOBFlightTypeState: FlightType = FlightType.ROUND_TRIP;

  @observable
  public listOfAdditionalLegs: FlightLegState[];

  @observable
  public multiCityFlightLegURL: string[];

  @observable
  public partialStayDate: DateState;

  @observable
  public packageDates: DateState;

  public moduleName = "";

  @observable
  public trackingEnabled = true;

  @computed
  public get date() {
    return this.packageDates;
  }

  @observable
  public packageTravelers: TravelersState;

  @computed
  public get travelers() {
    return this.packageTravelers;
  }

  @computed
  public get hotelPackageTravelersMetadata() {
    return travelersMetadata(this.travelers.hotelPackage);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  //validations
  @observable
  public originInvalidKey: string | "";

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateOriginField = () => {
    if (this.subLOBState !== "hc" && !this.globalState.validateOriginField()) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("origin")!;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () =>
    this.validateDestinationFieldHasValue() && this.validateOriginDifferentFromDestination();

  @action
  public validateDestinationFieldHasValue = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      // if the origin field is valid or if subLob does not have origin field I want to focus the invalid destination
      if (this.globalState.validateOriginField() || this.subLOBState === "hc") {
        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("destination")!;
        }
      }

      return false;
    }
    this.destinationInvalidKey = "";

    return true;
  };

  @action
  public validateOriginDifferentFromDestination = () => {
    if (!this.globalState.validateOriginDifferentFromDestination() && this.subLOBState !== "hc") {
      this.destinationInvalidKey = this.config.location.destination.invalidOriginSameAsDestinationKey!;
      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("destination")!;
      }

      return false;
    }
    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public hotelTravelersInvalidKey: string | "";

  @observable
  public nonHotelTravelersInvalidKey: string | "";

  @action
  public validateLessThanNTravelers = () => {
    let isValid = false;
    switch (this.subLOBState) {
      case "fc":
        isValid = this.globalState.validateLessThanNTravelers(
          this.nonHotelTravelersMetadata,
          this.config.subLOB.flightCar.travelers
        );
        if (!isValid) {
          this.nonHotelTravelersInvalidKey = this.config.subLOB.flightCar.travelers.invalidLessThanNTravelersMessageToken;

          return false;
        }
        break;
      case "hc":
        isValid = this.globalState.validateLessThanNTravelers(
          this.hotelPackageTravelersMetadata,
          this.config.subLOB.hotelCar.travelers
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.subLOB.hotelCar.travelers.invalidLessThanNTravelersMessageToken;

          return false;
        }
        break;
      default:
        isValid = this.globalState.validateLessThanNTravelers(
          this.hotelPackageTravelersMetadata,
          this.config.travelers
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

          return false;
        }
    }

    return true;
  };

  @action
  public validateUnattendedInfantInLap = () => {
    let isValid = false;
    switch (this.subLOBState) {
      case "fc":
        isValid = this.globalState.validateUnattendedInfantInLap(
          this.nonHotelTravelersMetadata,
          this.config.subLOB.flightCar.travelers,
          this.travelers.nonHotel.infantSeating
        );
        if (!isValid) {
          this.nonHotelTravelersInvalidKey = this.config.subLOB.flightCar.travelers.invalidUnattendedInfantOnLapMessageToken;

          return false;
        }
        break;
      case "hc":
        isValid = this.globalState.validateUnattendedInfantInLap(
          this.hotelPackageTravelersMetadata,
          this.config.subLOB.hotelCar.travelers,
          this.travelers.hotel.infantSeating
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.subLOB.hotelCar.travelers.invalidUnattendedInfantOnLapMessageToken;

          return false;
        }
        break;
      default:
        isValid = this.globalState.validateUnattendedInfantInLap(
          this.hotelPackageTravelersMetadata,
          this.config.travelers,
          this.travelers.hotel.infantSeating
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.travelers.invalidUnattendedInfantOnLapMessageToken;

          return false;
        }
    }

    return true;
  };

  @action
  public validateInfantsPerTraveler = () => {
    let isValid = false;
    switch (this.subLOBState) {
      case PackagesSubLobs.FLIGHT_CAR:
        isValid = this.globalState.validateInfantsPerTraveler(
          this.nonHotelTravelersMetadata,
          this.config.subLOB.flightCar.travelers,
          this.travelers.nonHotel.infantSeating
        );
        if (!isValid) {
          this.nonHotelTravelersInvalidKey = this.config.travelers.invalidNumberOfInfantPerTravelerMessageToken!;

          return false;
        }
        break;
      case PackagesSubLobs.HOTEL_CAR:
        isValid = this.globalState.validateUnattendedInfantInLap(
          this.hotelPackageTravelersMetadata,
          this.config.subLOB.hotelCar.travelers,
          this.travelers.hotel.infantSeating
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.subLOB.hotelCar.travelers.invalidUnattendedInfantOnLapMessageToken;

          return false;
        }
        break;
      default:
        isValid = this.globalState.validateInfantsPerTraveler(
          this.hotelPackageTravelersMetadata,
          this.config.travelers,
          this.travelers.hotel.infantSeating
        );
        if (!isValid) {
          this.hotelTravelersInvalidKey = this.config.travelers.invalidNumberOfInfantPerTravelerMessageToken!;

          return false;
        }
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers() && this.validateUnattendedInfantInLap();

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @action
  public validateMaxDateRange = () => {
    switch (this.subLOBState) {
      case "hc":
        if (
          !this.globalState.validateMaxDateRange(
            this.date.start,
            this.date.end,
            this.config.subLOB.hotelCar.date.maxDaysRange
          )
        ) {
          this.dateEndInvalidKey = this.config.subLOB.hotelCar.date.invalidMaxDatesRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }
        break;
      default:
        if (
          this.isRoundtripFlight() &&
          !this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)
        ) {
          this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @observable
  public partialStayStartDateInvalidKey = "";

  @action
  public validatePartialStayStartDateWithinRange = () => {
    if (!this.isPartialStay()) {
      return true;
    }

    switch (this.subLOBState) {
      case "":
      case "fh":
      case "fhc":
        let startDate = this.date.start;
        let endDate = this.date.end;
        if (this.isMulticityFlight()) {
          const legsDates = this.getLegsDates();
          startDate = legsDates.shift() || startDate;
          endDate = legsDates.length >= 1 ? legsDates.pop() || endDate : this.partialStayDate.end;
        } else if (this.isOnewayFlight()) {
          endDate = dateState(this.config.date.daysBookableInAdvance).end; // max days
        }

        if (!this.globalState.validatePartialDateWithinRange(startDate, endDate, this.partialStayDate.start)) {
          this.partialStayStartDateInvalidKey = this.retrieveConfigPartialDates().invalidStartDateOutOfRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d1")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validatePartialStayStartDateField = () => this.validatePartialStayStartDateWithinRange();

  @observable
  public partialStayEndDateInvalidKey = "";

  @action
  public validatePartialStayMaxDateRange = () => {
    switch (this.subLOBState) {
      case "":
      case "fh":
      case "fhc":
        if (
          this.isPartialStay() &&
          !this.globalState.validateMaxDateRange(
            this.partialStayDate.start,
            this.partialStayDate.end,
            this.config.partialStayDate.maxDaysRange
          )
        ) {
          this.partialStayEndDateInvalidKey = this.config.partialStayDate.invalidMaxDatesRangeMessageToken;
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("d2")!;
          }

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validatePartialStayEndDateWithinRange = () => {
    if (!this.isPartialStay()) {
      return true;
    }

    switch (this.subLOBState) {
      case "":
      case "fh":
      case "fhc":
        let startDate = this.date.start;
        let endDate = this.date.end;
        if (this.isOnewayFlight()) {
          endDate = this.newDateFrom(this.partialStayDate.end);
          startDate = this.newDateFrom(this.date.start);
          if (startDate >= endDate) {
            this.partialStayEndDateInvalidKey = this.config.onewayFlight.partialStayDate.invalidEndDateMessageToken;

            return false;
          }
        } else if (this.isMulticityFlight()) {
          const legsDates = this.getLegsDates(); // all legs
          startDate = legsDates.shift() || startDate;
          endDate = legsDates.length >= 1 ? legsDates.pop() || endDate : this.partialStayDate.end;
        }

        if (!this.globalState.validatePartialDateWithinRange(startDate, endDate, this.partialStayDate.end)) {
          this.partialStayEndDateInvalidKey = this.retrieveConfigPartialDates().invalidEndDateOutOfRangeMessageToken;

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  private retrieveConfigPartialDates() {
    if (this.isOnewayFlight()) {
      return this.config.onewayFlight.partialStayDate;
    }
    if (this.isMulticityFlight()) {
      return this.config.multiCityFlight.partialStayDate;
    }

    return this.config.partialStayDate;
  }

  public validatePartialStayEndDateField = () =>
    this.validatePartialStayEndDateWithinRange() && this.validatePartialStayMaxDateRange();

  @action
  public validateLobCombinationState = () => {
    return this.subLOBState !== "error";
  };

  @observable
  public legsDateStartInvalidKey: string[];

  @action
  public validateDepatureDates = (): boolean => {
    if (this.isMulticityFlight()) {
      let previous: Date = this.newDateFrom(this.date.start);
      for (const [index, leg] of this.listOfAdditionalLegs.entries()) {
        if (!leg.location.origin.metaData.regionId && !leg.location.destination.metaData.regionId) {
          continue; // no validation if location is empty
        }
        const legDate = this.newDateFrom(leg.date.start);
        if (legDate.getTime() >= previous.getTime()) {
          previous = legDate;
          this.legsDateStartInvalidKey[index] = "";
        } else {
          this.legsDateStartInvalidKey[index] = locKeys.error.flightInvalidDepartureDate;
        }
      }

      return !this.legsDateStartInvalidKey.includes(locKeys.error.flightInvalidDepartureDate);
    }

    const maxNumberOfAdditionalLegs = this.config.multiCityFlight.maxNumberOfAdditionalLegs;
    this.legsDateStartInvalidKey = new Array<string>(maxNumberOfAdditionalLegs).fill("", 0, maxNumberOfAdditionalLegs);

    return true;
  };

  @observable
  public hotelDestinationInvalidKey = "";

  @action
  public validateHotelDestination = (): boolean => {
    let validHotelDestination = true;

    if (!this.hotelLocation?.destination?.metaData?.regionId) {
      // fh + fssse + multicity/oneway
      if (this.isPackageFlightHotel() && this.isStaySomewhereElse() && !this.isRoundtripFlight()) {
        if (
          (this.isMulticityFlight() && this.experimentNewPackageMulticityFlightType.variant1) ||
          (this.isOnewayFlight() && this.experimentNewPackageOnewayFlightType.variant1)
        ) {
          if (!this.errorInputRef) {
            this.errorInputRef = this.findFocusElement("destination")!;
          }

          validHotelDestination = false;
        }
      } else if (this.isPackageHotelCar() && this.driveSomewhereElse) {
        // hc + different pickup location
        if (!this.errorInputRef) {
          this.errorInputRef = this.findFocusElement("destination")!;
        }

        validHotelDestination = false;
      }
    }

    this.hotelDestinationInvalidKey = validHotelDestination ? "" : locKeys.error.staySomewhereElseInvalidDestination;

    return validHotelDestination;
  };

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelDestinationInvalidKey = "";
    this.partialStayStartDateInvalidKey = "";
    this.partialStayEndDateInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
    const maxNumberOfAdditionalLegs = this.config.multiCityFlight.maxNumberOfAdditionalLegs;
    this.legsDateStartInvalidKey = new Array<string>(maxNumberOfAdditionalLegs).fill("", 0, maxNumberOfAdditionalLegs);
  };

  public validateForm = () => {
    this.cleanErrorState();

    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateTravelersField(),
      this.validateEndDateField(),
      this.validateDepatureDates(),
      this.validateHotelDestination(),
      this.validatePartialStayStartDateField(),
      this.validatePartialStayEndDateField(),
      this.validateLobCombinationState(),
    ];

    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  private updatePackageDates() {
    this.packageDates.start = this.date.start;

    if (this.isOnewayFlight()) {
      this.packageDates.end = this.partialStayDate.end;
    } else if (this.isMulticityFlight()) {
      const legsDates = this.getLegsDates();

      this.packageDates.end = legsDates.pop() || this.packageDates.end;
    } else {
      // roundtrip
      this.packageDates.end = this.date.end;
    }
  }

  private getLegsDates(): Date[] {
    const legsDates = new Array<Date>(this.date.start);
    if (this.listOfAdditionalLegs) {
      this.listOfAdditionalLegs.forEach((leg: FlightLegState) => {
        if (leg.location.origin.metaData.regionId && leg.location.destination.metaData.regionId) {
          legsDates.push(leg.date.start);
        }
      });
    }

    legsDates.sort((a: Date, b: Date) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });

    return legsDates;
  }

  @action
  public updateDateSelection = (start: Date, end: Date, multiLegIndex?: number) => {
    if (multiLegIndex !== undefined) {
      this.updateLegDate(start, end, multiLegIndex);
    } else {
      const previousDepatureDate = new Date(this.date.start);

      this.copyDepartureReturningDatesToHotel(start, end);
      this.date.start = start;
      this.date.end = end;

      this.dateStartInvalidKey = "";
      this.dateEndInvalidKey = "";
      this.partialStayStartDateInvalidKey = "";
      this.partialStayEndDateInvalidKey = "";

      // update additionals legs (if exist) when first row change.
      if (this.listOfAdditionalLegs && this.listOfAdditionalLegs.length > 0) {
        this.updateLegsDates(previousDepatureDate, start, end, 0);
      }
      this.updatePackageDates();
    }
  };

  @action
  public updatePartialStayDateSelection = (start: Date, end: Date) => {
    this.partialStayDate.start = start;
    this.partialStayDate.end = end;
    this.partialStayStartDateInvalidKey = "";
    this.partialStayEndDateInvalidKey = "";

    this.updatePackageDates();
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection, index?: number) => {
    //in the case of a multi-city flight, update the individual leg
    if (index !== undefined) {
      this.updateLegLocationOrigin(selection, index);
    } else {
      this.globalState.updateOriginSelection(selection);
      this.originInvalidKey = "";
    }
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection, index?: number) => {
    if (index !== undefined) {
      this.updateLegLocationDestination(selection, index);
    } else {
      this.globalState.updateDestinationSelection(selection);
      this.destinationInvalidKey = "";

      // copy hotel destination if empty and fssse
      if (this.isStaySomewhereElse() && !this.globalState.hotelLocation.destination.metaData.regionId) {
        this.updateHotelDestinationSelection(selection);
      }
    }
  };

  @action
  public updateHotelDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateHotelDestinationSelection(selection);
  };

  @action
  private copyDepartureReturningDatesToHotel(start: Date, end: Date) {
    if (this.isPartialStay()) {
      this.copyDepartureToHotelCheckIn(start);
      this.copyReturningToHotelCheckOut(end);
    }
  }

  private copyDepartureToHotelCheckIn(start: Date) {
    // copy flight dates to hotel dates if they are the same
    const previousStartDate = this.newDateFrom(this.date.start);
    const previousStartPartialStay = this.newDateFrom(this.partialStayDate.start);
    if (previousStartDate.getTime() === previousStartPartialStay.getTime()) {
      this.partialStayDate.start = start;
    }
  }

  private copyReturningToHotelCheckOut(end: Date) {
    const previousEndDate = this.newDateFrom(this.date.end);
    const previousEndPartialStay = this.newDateFrom(this.partialStayDate.end);
    if (previousEndDate.getTime() === previousEndPartialStay.getTime()) {
      this.partialStayDate.end = end;
    } else if (this.partialStayDate.end < this.partialStayDate.start) {
      const newEnd = new Date(this.partialStayDate.start);
      this.partialStayDate.end = new Date(newEnd.setDate(newEnd.getDate() + this.config.defaultLengthInDays));
    }
  }

  @action
  public swapLocations(index?: number) {
    if (index !== undefined) {
      this.swapOriginAndDestinationLocations(index);
    } else {
      this.globalState.swapOriginAndDestinationLocations();
    }
  }

  @action
  public toggleStaySomewhereElse() {
    this.staySomewhereElse = !this.staySomewhereElse;
  }

  @action
  public toggleDriveSomewhereElse() {
    const originalDestination = this.copyLocationDestination(this.globalState.location.destination);
    const targetDestination = this.copyLocationDestination(this.globalState.hotelLocation.destination);
    if (originalDestination?.metaData?.regionId) {
      this.globalState.hotelLocation = hotelLocationState();
      this.globalState.hotelLocation.destination = originalDestination;
    }
    if (targetDestination?.metaData?.regionId) {
      this.globalState.location = locationState();
      this.globalState.location.destination = targetDestination;
    }
    this.driveSomewhereElse = !this.driveSomewhereElse;
  }

  private copyLocationDestination(destination: Location) {
    return {
      value: destination?.value || "",
      metaData: {
        countryCode: destination?.metaData?.countryCode || "",
        latLong: destination?.metaData?.latLong || "",
        ttla: destination?.metaData?.ttla || "",
        shortName: destination?.metaData?.shortName || "",
        regionId: destination?.metaData?.regionId || "",
      },
    };
  }

  @action
  public togglePartialStay() {
    this.partialStay = !this.partialStay;
    setHotelPartialStayDate(this.date, this.partialStayDate, this.config.defaultLengthInDays);
  }

  @action
  public setFlightClass(flightClassCode: ClassCodes) {
    this.globalState.setFlightClass(flightClassCode);
  }

  @action
  public setFlightAirline(flightAirline: PreferredAirline) {
    this.globalState.setFlightAirline(flightAirline);
  }

  @action
  public setFlightType(flightTypeCode: FlightType) {
    this.updateSubLOBFlightTypeState(flightTypeCode);
    this.globalState.setFlightType(flightTypeCode);
  }

  @action
  public addALeg = () => {
    if (this.listOfAdditionalLegs.length < this.config.multiCityFlight.maxNumberOfAdditionalLegs) {
      let legDateState = {
        start: this.date.start,
        end: this.date.end,
      };

      if (this.listOfAdditionalLegs.length > 0) {
        legDateState = {
          start: this.listOfAdditionalLegs[this.listOfAdditionalLegs.length - 1].date.start,
          end: this.listOfAdditionalLegs[this.listOfAdditionalLegs.length - 1].date.end,
        };
      }

      this.listOfAdditionalLegs.push({ location: locationState(), date: legDateState });
      this.onewayFlight = false;
      this.setFlightType(FlightType.MULTI_CITY);
    }
  };

  @action
  public removeALeg = (index: number) => {
    if (index < this.listOfAdditionalLegs.length) {
      this.listOfAdditionalLegs.splice(index, 1);
    }
  };

  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = subLOBState;
    this.updateEssAdapterConfigPackageType(subLOBState);
  }

  @action
  public updateSubLOBFlightTypeState(subLOBState: FlightType) {
    this.subLOBFlightTypeState = subLOBState;
    this.globalState.setFlightType(subLOBState);

    if (subLOBState === FlightType.MULTI_CITY && this.listOfAdditionalLegs.length === 0) {
      this.listOfAdditionalLegs = [
        {
          location: locationState(),
          date: this.globalState.getDateFromConfig(this.config.date),
        },
      ];
    }
  }

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers, this.travelers);

    if (this.lastSearchData && this.lastSearchData.travelers && this.validateTravelersField()) {
      this.lastSearchData.travelers = JSON.stringify(this.travelers);
    }

    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers, this.travelers);

    if (this.lastSearchData && this.lastSearchData.travelers && this.validateTravelersField()) {
      this.lastSearchData.travelers = JSON.stringify(this.travelers);
    }

    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string,
    timestamp?: number
  ) {
    this.globalState.updateDestinationFromCookie(
      value,
      regionID,
      countryCode,
      latLong,
      ttla,
      destinationID,
      hotelID,
      timestamp
    );
    this.destinationInvalidKey = "";
  }

  @action
  public updateDestination(
    value: string,
    regionId: string,
    countryCode: string | undefined = "",
    latLong: string | undefined = "",
    ttla: string | undefined = "",
    destinationID: string | undefined = "",
    hotelID: string | undefined = ""
  ) {
    this.updateDestinationFromCookie(value, regionId, countryCode, latLong, ttla, destinationID, hotelID);
  }

  @action
  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla, timestamp);
    this.originInvalidKey = "";
  }

  @action
  public updateHotelDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateHotelDestinationFromCookie(value, regionID, countryCode, latLong, ttla);
  }

  @action
  public updateOnewayFlag(value: boolean) {
    this.onewayFlight = value;
  }

  @action
  public updateExperimentNewPackageVariant(context: ExtendedContextStore) {
    const onewayPackageExperimentVariant1 = isVariantEnabled(context, Experiment.New_Package_Flight_Type_Oneway, 1);
    this.experimentNewPackageOnewayFlightType = {
      control: !onewayPackageExperimentVariant1,
      variant1: onewayPackageExperimentVariant1,
    };

    const multicityPackageExperimentVariant1 = isVariantEnabled(
      context,
      Experiment.New_Package_Flight_Type_Multicity,
      1
    );
    this.experimentNewPackageMulticityFlightType = {
      control: !multicityPackageExperimentVariant1,
      variant1: multicityPackageExperimentVariant1,
    };
  }

  private newDateFrom(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  @computed
  public get flightTypeCodeForURL() {
    return FlightTypeCodeMap[this.flightTypeCode] || FlightTypeCodeMap.default;
  }

  public isPackageFlightHotel() {
    return this.subLOBState === "" || this.subLOBState === "fh";
  }

  public isOnewayFlight(): boolean {
    return this.flightTypeCode === FlightType.ONE_WAY || this.onewayFlight;
  }

  public isMulticityFlight(): boolean {
    return this.flightTypeCode === FlightType.MULTI_CITY;
  }

  public isRoundtripFlight(): boolean {
    return this.flightTypeCode === FlightType.ROUND_TRIP;
  }

  public isPackageHotelCar(): boolean {
    return this.subLOBState === "hc";
  }

  public isPartialStay(): boolean {
    return this.partialStay || this.isOnewayFlight() || this.isMulticityFlight();
  }

  public isStaySomewhereElse(): boolean {
    return this.staySomewhereElse || this.isOnewayFlight() || this.isMulticityFlight();
  }

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const hotelDestination = this.hotelLocation.destination;
      const hotelDestinationMetaData = this.hotelLocation.destination.metaData;
      const date = this.date;
      const partialStayDate = this.partialStayDate;
      const listOfAddtionalLegs = this.listOfAdditionalLegs;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        package: {
          origin: {
            value: origin.value || "",
            regionID: originMetaData.regionId || "",
            ttla: originMetaData.ttla || "",
            countryCode: originMetaData.countryCode || "",
            latLong: originMetaData.latLong || "",
            destinationID: originMetaData.destinationId || "",
            hotelID: originMetaData.hotelId || "",
          },
          destination: {
            value: destination.value || "",
            regionID: destinationMetaData.regionId || "",
            ttla: destinationMetaData.ttla || "",
            countryCode: destinationMetaData.countryCode || "",
            latLong: destinationMetaData.latLong || "",
            destinationID: destinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          hotel_destination: {
            value: hotelDestination.value || "",
            regionID: hotelDestinationMetaData.regionId || "",
            ttla: hotelDestinationMetaData.ttla || "",
            countryCode: hotelDestinationMetaData.countryCode || "",
            latLong: hotelDestinationMetaData.latLong || "",
            destinationID: hotelDestinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          partial: {
            start_date: partialStayDate.start ? partialStayDate.start.toString() : "",
            end_date: partialStayDate.end ? partialStayDate.end.toString() : "",
            stay_check: this.partialStay.toString(),
          },
          flight_code: {
            class: this.flightClassCode,
            type: this.flightTypeCode,
          },
          subLOBState: this.subLOBState,
          subLOBFlightTypeState: this.subLOBFlightTypeState,
          list_of_additional_legs: listOfAddtionalLegs ? JSON.stringify(listOfAddtionalLegs) : "",
          travelers: JSON.stringify(this.travelers),
          timestamp: Math.floor(Date.now() / 1000),
        },
      };

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
        days: 30,
      });
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && lastSearchData && this.useLastSearch) {
      const originValue = decodeURIComponent(lastSearchData.origin?.value);
      const destinationValue = decodeURIComponent(lastSearchData.destination?.value);
      const hotelDestinationValue = decodeURIComponent(lastSearchData.hotel_destination?.value);

      const startDateFromCookie = lastSearchData.date?.start ? new Date(lastSearchData.date.start) : new Date();
      const endDateFromCookie = lastSearchData.date?.end ? new Date(lastSearchData.date.end) : new Date();
      const partialStartDateFromCookie = lastSearchData.partial?.start_date
        ? new Date(lastSearchData.partial.start_date)
        : new Date();
      const partialEndDateFromCookie = lastSearchData.partial?.end_date
        ? new Date(lastSearchData.partial.end_date)
        : new Date();

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;
      const partialStartDateToUse = partialStartDateFromCookie >= today ? partialStartDateFromCookie : today;
      const partialEndDateToUse = partialEndDateFromCookie >= tomorrow ? partialEndDateFromCookie : tomorrow;

      // Check if the lastSearch cookie passed had packageWizardState and travelers info, if not reload fallback from globalWizard
      const travelers: TravelersState = lastSearchData.travelers
        ? JSON.parse(lastSearchData.travelers)
        : this.travelers;

      this.updateOriginFromCookie(
        originValue !== "undefined" ? originValue : "",
        lastSearchData.origin?.regionID ? lastSearchData.origin.regionID : "",
        lastSearchData.origin?.countryCode ? lastSearchData.origin.countryCode : "",
        lastSearchData.origin?.latLong ? lastSearchData.origin.latLong : "",
        lastSearchData.origin?.ttla ? lastSearchData.origin.ttla : "",
        lastSearchData.timestamp
      );

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
        lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
        lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
        lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
        lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
        lastSearchData.destination?.hotelID ? lastSearchData.destination?.hotelID : "",
        lastSearchData.timestamp
      );

      this.updateHotelDestinationFromCookie(
        hotelDestinationValue !== "undefined" ? hotelDestinationValue : "",
        lastSearchData.hotel_destination?.regionID ? lastSearchData.hotel_destination.regionID : "",
        lastSearchData.hotel_destination?.countryCode ? lastSearchData.hotel_destination.countryCode : "",
        lastSearchData.hotel_destination?.latLong ? lastSearchData.hotel_destination.latLong : "",
        lastSearchData.hotel_destination?.ttla ? lastSearchData.hotel_destination.ttla : ""
      );

      this.updateDateSelection(startDateToUse, endDateToUse);

      this.setFlightClass(lastSearchData.flight_code?.class ? lastSearchData.flight_code.class : this.flightClassCode);

      this.setFlightType(lastSearchData.flight_code?.type ? lastSearchData.flight_code.type : this.flightTypeCode);

      this.partialStay = lastSearchData.partial?.stay_check
        ? JSON.parse(lastSearchData.partial.stay_check)
        : this.partialStay;

      if (this.isPartialStay()) {
        this.updatePartialStayDateSelection(partialStartDateToUse, partialEndDateToUse);
      }

      if (this.subLOBState !== PackagesSubLobs.FLIGHT_CAR) {
        const hotelTravelers = travelers.hotelPackage;
        this.updateHotelTravelersSelection(hotelTravelers);
      } else {
        const nonHotelTravelers = travelers.nonHotel;
        this.updateNonHotelTravelersSelection(nonHotelTravelers);
      }

      if (lastSearchData.list_of_additional_legs) {
        this.listOfAdditionalLegs = JSON.parse(lastSearchData.list_of_additional_legs).map((leg: FlightLegState) => {
          return {
            ...leg,
            date: {
              start: new Date(leg.date.start),
              end: new Date(leg.date.end),
            },
          };
        });
      } else {
        this.listOfAdditionalLegs = new Array<FlightLegState>(this.config.multiCityFlight.maxNumberOfAdditionalLegs);
      }
      this.updateSubLOBState(lastSearchData.subLOBState);
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.updateInvalidFormsState(false);
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
      this.updateInvalidFormsState(true);
    }
    this.globalState.submit();
  };

  /**
   * Cleans error state in all forms
   */
  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
    this.isFlightCarFormValid = true;
    this.isFlightHotelCarFormValid = true;
    this.isFlightHotelDefaultFormValid = true;
    this.isFlightHotelMultiFormValid = true;
    this.isFlightHotelOneWayFormValid = true;
    this.isHotelCarFormValid = true;
  };

  @action
  public updateInvalidFormsState = (validState?: boolean) => {
    const state = validState !== undefined ? validState : true;
    switch (this.subLOBState) {
      case PackagesSubLobs.FLIGHT_CAR:
        this.isFlightCarFormValid = state;
        this.isFlightHotelCarFormValid = true;
        this.updateInvalidFormStateFlightHotel(true);
        this.isHotelCarFormValid = true;
        break;
      case PackagesSubLobs.FLIGHT_HOTEL_CAR:
        this.isFlightCarFormValid = true;
        this.isFlightHotelCarFormValid = state;
        this.updateInvalidFormStateFlightHotel(true);
        this.isHotelCarFormValid = true;
        break;
      case PackagesSubLobs.FLIGHT_HOTEL:
        this.isFlightCarFormValid = true;
        this.isFlightHotelCarFormValid = true;
        this.isHotelCarFormValid = true;
        this.updateInvalidFormStateFlightHotel(state);
        break;
      case PackagesSubLobs.HOTEL_CAR:
        this.isFlightCarFormValid = true;
        this.isFlightHotelCarFormValid = true;
        this.updateInvalidFormStateFlightHotel(true);
        this.isHotelCarFormValid = state;
        break;
      default:
        this.isFlightCarFormValid = true;
        this.isFlightHotelCarFormValid = true;
        this.updateInvalidFormStateFlightHotel(state);
        this.isHotelCarFormValid = true;
        break;
    }
  };

  @action
  public updateInvalidFormStateFlightHotel = (formState: boolean) => {
    switch (this.subLOBFlightTypeState) {
      case FlightType.ONE_WAY:
        this.isFlightHotelOneWayFormValid = formState;
        this.isFlightHotelMultiFormValid = true;
        this.isFlightHotelDefaultFormValid = true;
        break;
      case FlightType.MULTI_CITY:
        this.isFlightHotelOneWayFormValid = true;
        this.isFlightHotelMultiFormValid = formState;
        this.isFlightHotelDefaultFormValid = true;

        break;
      default:
        this.isFlightHotelDefaultFormValid = formState;
        this.isFlightHotelOneWayFormValid = true;
        this.isFlightHotelMultiFormValid = true;
        break;
    }
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  public updateEssAdapterConfigPackageType(subLOBState: string) {
    this.config.location.origin.essAdapterConfig.packageType = subLOBState;
    this.config.location.destination.essAdapterConfig.packageType = subLOBState;
  }

  constructor(config: PackageConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    this.packageTravelers = travelersStateOnConfig(this.config.travelers);
    this.packageDates = this.globalState.getDateFromConfig(this.config.date);
    this.partialStayDate = this.globalState.getDateFromConfig(this.config.date);

    this.listOfAdditionalLegs = [];
    const maxNumberOfAdditionalLegs = this.config.multiCityFlight.maxNumberOfAdditionalLegs;
    this.legsDateStartInvalidKey = new Array<string>(maxNumberOfAdditionalLegs).fill("", 0, maxNumberOfAdditionalLegs);
    this.multiCityFlightLegURL = new Array<string>(maxNumberOfAdditionalLegs);
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.package;
    }
  }
}
